import { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save } from "lucide-react";

export default function Company() {
  const [form, setForm] = useState(null);

  useEffect(() => {
    api.get("/company").then((r) => setForm(r.data));
  }, []);

  if (!form) return <div className="text-slate-400 py-16 text-center">Loading…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/company", form);
      toast.success("Company profile saved");
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
  };

  const Section = ({ title, children }) => (
    <div className="bg-white border border-slate-200 rounded-md">
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
        <div className="font-display font-bold tracking-tight text-slate-900">{title}</div>
      </div>
      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>
    </div>
  );

  const F = ({ label, k, type = "text", area, options }) => (
    <div className={`space-y-1.5 ${area ? "md:col-span-3" : ""}`}>
      <Label className="text-xs font-semibold uppercase tracking-wider">{label}</Label>
      {options ? (
        <Select value={form[k] || ""} onValueChange={(v) => set(k, v)}>
          <SelectTrigger className="h-10" data-testid={`company-${k}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : area ? (
        <Textarea rows={2} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`company-${k}`} />
      ) : (
        <Input type={type} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-10" data-testid={`company-${k}`} />
      )}
    </div>
  );

  return (
    <form onSubmit={save} className="space-y-4" data-testid="company-page">
      <Section title="Company">
        <F label="Company" k="company" />
        <F label="Code" k="code" />
        <F label="Active" k="active" options={["ACTIVE", "INACTIVE"]} />
        <F label="GST No" k="gst_no" />
        <F label="SAC Code" k="sac_code" />
        <F label="FSSAI Code" k="fssai_code" />
        <F label="Owner" k="owner" />
        <F label="Email" k="email" type="email" />
        <F label="Phone" k="phone" />
        <F label="Mobile" k="mobile" />
      </Section>
      <Section title="Bank Details">
        <F label="Bank Account Name" k="bank_account_name" />
        <F label="Bank Account No" k="bank_account_no" />
        <F label="Bank Name" k="bank_name" />
        <F label="Bank IFSC" k="bank_ifsc" />
        <F label="Bank Branch" k="bank_branch" />
      </Section>
      <Section title="Billing">
        <F label="Bill Company Name" k="bill_company_name" area />
        <F label="Company Description" k="description" area />
        <F label="Address" k="address" area />
        <F label="Working Hours (shown on receipt)" k="working_hours" area />
      </Section>
      <div className="flex justify-end">
        <Button type="submit" className="bg-[color:var(--brand-teal)] hover:bg-[color:var(--brand-teal-hover)] text-white h-10" data-testid="company-save">
          <Save className="h-4 w-4 mr-1.5" /> Save Company
        </Button>
      </div>
    </form>
  );
}
